import * as yup from 'yup';
import { SchemaDescription } from 'yup';
import { KpiConfigPeriod } from '../data-models/company-financials.data-model';
import { IField, KpiConfigSection } from '../data-models/field2.data-model';
import {
  FORMULA_COMPANY_RECORD,
  FORMULA_FIELD_ENTITIES,
  FORMULA_ROW_RECORD,
  FormulaFieldEntity,
  formulaFieldPath,
  formulaFieldsForEntity,
  FormulaRecord,
  IFormulaFieldDataModel,
} from '../data-models/formula-field.data-model';
import { COMPANY_VIEW_TYPE } from '../data-models/view-config.data-model';
import {
  KPI_PERIOD_KEYS,
  KPI_SECTION_KEYS,
  KpiPeriodKey,
  KpiSectionKey,
  kpiSectionLabel,
  ValueType,
} from '../formula-engine';
import { companyFields } from '../schemas/Company.schema';
import { metricsSchema } from '../schemas/Metrics.schema';
import { roundTrackerFields } from '../schemas/RoundTracker.schema';
import { SecurityReportSchema } from '../schemas/SecurityReport.schema';
import { ColumnMeta } from '../types';
import { schemaFieldDataType } from './schema-data-type';
import { camelCaseToCapitalizedString } from './stringUtils';

export type FormulaFieldSource = 'metric' | 'company' | 'round' | 'security' | 'formula' | 'kpi';

/** One entry the formula language knows about: a record to navigate, or a field to reference. */
export interface FieldRefTarget {
  /**
   * Canonical resolution path: "<record>.<key>" for a record's field
   * ("metrics.fmv", "company.fund"), "<list path>.<member>" for a list
   * item's field, "formulaData.<key>" / "kpiData.<id>" for the root-level
   * imports, or the bare record name for a record.
   */
  path: string;
  /**
   * What the chip and the menu show — the display name under its record
   * ("Metrics.FMV", "Company.Investment Amount"), a member under its list
   * ("Transactions.Distributions"), a root import on its own ("Gross MOIC").
   * Brackets are the one thing a display name loses (see displaySegment).
   */
  displayPath: string;
  displayName: string;
  source: FormulaFieldSource;
  /**
   * yup primitive ('number' | 'string' | 'boolean' | ...), 'list' for a
   * row array of records, or 'object' for a record.
   */
  type: string;
  /**
   * Whether formulas may reference this entry as a value: number and string
   * fields, except id-selects — string fields whose stored value is an
   * internal id (e.g. Owner holds a user id), where comparisons against the
   * displayed text would silently never match — plus lists (as REDUCE's
   * second argument) and their number/string members. Records are never
   * values. Non-referenceable entries stay in the indexes so hand-typed refs
   * get a typed error instead of "unknown".
   */
  referenceable: boolean;
  /**
   * The record or list this entry is a direct child of (its canonical path),
   * so a menu can list a record's keys; absent on records and root imports.
   */
  parent?: string;
  /**
   * The path a stored formula from before records may still use — the row
   * key on its own ("fmv" for "metrics.fmv"). Resolves like the path.
   */
  legacyPath?: string;
  /** Set on a list item's member ("metrics.transactions.distributions"): the list it belongs to. */
  list?: { path: string; displayName: string; displayPath: string };
  /** Set on a KPI entry ("kpiData.<id>"): the period/section tokens its config supports. */
  kpi?: { periods: KpiPeriodKey[]; sections: KpiSectionKey[] };
  /** Set on the synthesized KPI period/scenario argument targets ([Quarter], [Actual]). */
  kpiToken?: boolean;
  /** From the matching /column meta when one exists; schema-only fields have none. */
  description?: string;
}

export interface FormulaVocabulary {
  /** The records the editor navigates ("[" lists them; "." descends). */
  records: FieldRefTarget[];
  /** The referenceable subset (typeahead leaves, chips). */
  referenceable: FieldRefTarget[];
  /** Lowercased normalized display name → candidates, over every field (typed errors need the rest too). */
  displayNameIndex: Map<string, FieldRefTarget[]>;
  /** Display-path key (see displayPathKey) → entries, over every field and record. */
  displayPathIndex: Map<string, FieldRefTarget[]>;
  /** Exact path → entry, over every field and record; legacy paths resolve too, canonical ones winning a collision. */
  byPath: Map<string, FieldRefTarget>;
}

export const FORMULA_RECORD_LABEL: Record<FormulaRecord, string> = {
  metrics: 'Metrics',
  company: 'Company',
  round: 'Round',
  security: 'Security',
};

const RECORD_SOURCE: Record<FormulaRecord, FormulaFieldSource> = {
  metrics: 'metric',
  company: 'company',
  round: 'round',
  security: 'security',
};

/**
 * A display name as one segment of a display path: as is, minus brackets —
 * the display rendering is parsed, and a "[" or "]" inside a ref would break
 * the formula around it. (A dot inside a name stays; chips carry their path,
 * so only a hand-typed ref to such a name is ambiguous about its segments.)
 */
export function displaySegment(name: string): string {
  return name.replace(/[\][]/g, '').trim();
}

/** An entry's own segment of its display path; a name with nothing left falls back to its key. */
export function displaySegmentOf(target: Pick<FieldRefTarget, 'displayName' | 'path'>): string {
  return displaySegment(target.displayName) || target.path.slice(target.path.lastIndexOf('.') + 1);
}

/**
 * A display name as a lookup key: every run of characters outside A-Z, a-z,
 * 0-9 becomes "_" ("P&L ratio" → "P_L_ratio"), case kept — so a typed ref
 * matches whatever its spacing and punctuation.
 */
export function normalizeDisplaySegment(name: string): string {
  return name.replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

/**
 * A ref as a display-path lookup key: split on ".", each segment normalized
 * and lowercased — so "[Company.Investment Amount]" and
 * "[company.investment_amount]" both find "Company.Investment Amount".
 */
export function displayPathKey(ref: string): string {
  return ref
    .split('.')
    .map((segment) => normalizeDisplaySegment(segment).toLowerCase())
    .filter(Boolean)
    .join('.');
}

/**
 * Prettified keys leave acronyms half-cased ("Ltm Ebitda"); fix the words we
 * know. Applied after column-name overrides, so it only touches fallbacks.
 */
const ACRONYM_WORDS = new Map(
  [
    'FMV',
    'MOIC',
    'IRR',
    'DPI',
    'TVPI',
    'RVPI',
    'EBITDA',
    'LTM',
    'CY',
    'CY2',
    'GL',
    'PPS',
    'NAV',
    'FDS',
    'RTFE',
    'KPI',
    'FYE',
    'ROC',
  ].map((word) => [word.toLowerCase(), word] as const)
);

function fixAcronyms(label: string): string {
  return label
    .split(' ')
    .map((word) => ACRONYM_WORDS.get(word.toLowerCase()) ?? word)
    .join(' ');
}

/** Id-ish number fields (companyId, boardDirectorId, ...) are join keys, not metrics. */
function isIdField(key: string): boolean {
  return key === 'id' || /Id$/.test(key);
}

interface DescribedField {
  type?: string;
  label?: string;
  meta?: { rendererMeta?: { values?: { value?: unknown; displayName?: unknown }[] } };
  innerType?: { type?: string; fields?: Record<string, unknown> };
}

/**
 * A select whose option values differ from their labels stores ids/codes
 * (e.g. Owner → user id), so the raw row value never matches the text users
 * would compare against. Same-valued selects (status enums) are fine.
 */
function isIdSelect(description: DescribedField): boolean {
  const values = description.meta?.rendererMeta?.values;
  return Array.isArray(values) && values.some((option) => option?.value !== option?.displayName);
}

/** The scalar types a formula can hold; anything else stays known but not referenceable. */
const SCALAR_TYPES = new Set(['number', 'string', 'boolean', 'date']);

function scalarReferenceable(type: string, description: DescribedField): boolean {
  return SCALAR_TYPES.has(type) && !(type === 'string' && isIdSelect(description));
}

/**
 * A record to navigate, as opposed to a field: object-typed and at the root.
 * (A schema field can be object-typed too — it stays a non-referenceable
 * field under its record.)
 */
export function isFormulaRecord(target: Pick<FieldRefTarget, 'type' | 'parent'>): boolean {
  return target.type === 'object' && target.parent === undefined;
}

/** The record entry itself, ahead of its fields so display paths build parent-first. */
function recordEntry(record: FormulaRecord): FieldRefTarget {
  return {
    path: record,
    displayPath: FORMULA_RECORD_LABEL[record],
    displayName: FORMULA_RECORD_LABEL[record],
    source: RECORD_SOURCE[record],
    type: 'object',
    referenceable: false,
  };
}

/**
 * One entry per field of a record, under "<record>.<key>". A row array of
 * records becomes a 'list' entry plus one member entry per element field
 * ("metrics.transactions.distributions"), referenceable inside a REDUCE over
 * the list under the same number/string rules as row fields; company arrays
 * stay plain non-referenceable fields. The entity's own row record remembers
 * the unprefixed key stored formulas used before records (`legacyPath`).
 * Display paths are filled in once names are final (see withDisplayPaths).
 */
function describeFields(
  fields: Record<string, unknown>,
  record: FormulaRecord,
  isRowRecord: boolean
): FieldRefTarget[] {
  const source = RECORD_SOURCE[record];
  const entries: FieldRefTarget[] = [];
  for (const [key, rawDescription] of Object.entries(fields)) {
    if (isIdField(key)) {
      continue;
    }
    const description = rawDescription as DescribedField;
    // A string carrying an ISO date says so through its meta (see isoDateStringField).
    const type = schemaFieldDataType(rawDescription as SchemaDescription) ?? 'unknown';
    const path = `${record}.${key}`;
    const legacyPath = isRowRecord ? key : undefined;
    const displayName = description.label ?? camelCaseToCapitalizedString(key);
    const members =
      record !== FORMULA_COMPANY_RECORD && type === 'array' ? description.innerType?.fields : undefined;
    if (!members) {
      entries.push({
        path,
        displayPath: '',
        displayName,
        source,
        type,
        referenceable: scalarReferenceable(type, description),
        parent: record,
        legacyPath,
      });
      continue;
    }
    entries.push({
      path,
      displayPath: '',
      displayName,
      source,
      type: 'list',
      referenceable: true,
      parent: record,
      legacyPath,
    });
    for (const [memberKey, rawMember] of Object.entries(members)) {
      if (isIdField(memberKey)) {
        continue;
      }
      const member = rawMember as DescribedField;
      const memberType = schemaFieldDataType(rawMember as SchemaDescription) ?? 'unknown';
      entries.push({
        path: `${path}.${memberKey}`,
        displayPath: '',
        displayName: member.label ?? camelCaseToCapitalizedString(memberKey),
        source,
        type: memberType,
        referenceable: scalarReferenceable(memberType, member),
        parent: path,
        legacyPath: legacyPath && `${legacyPath}.${memberKey}`,
        list: { path, displayName, displayPath: '' },
      });
    }
  }
  return entries;
}

/**
 * The declared type of every ref the engine must read as something other
 * than what the row holds: schema fields and list members by canonical and
 * legacy path, and Date-typed formula fields (their result crosses the
 * boundary as ISO text). Other formula results and KPI values already carry
 * their type and need no entry.
 */
export function entityFieldTypes(
  entity: FormulaFieldEntity,
  formulaFields: IFormulaFieldDataModel[] = []
): (ref: string) => ValueType | undefined {
  const types = new Map<string, ValueType>();
  const add = (path: string | undefined, type: string) => {
    if (path !== undefined && VALUE_TYPES.has(type)) {
      types.set(path, type as ValueType);
    }
  };
  for (const entry of entityEntries(entity)) {
    add(entry.path, entry.type);
    add(entry.legacyPath, entry.type);
  }
  // A formula's result already carries its type when another formula reads it; only a date
  // crosses the boundary as ISO text and has to be read back as one. A field stored as Number
  // while its formula yields TRUE/FALSE (allowed before the type was storable) keeps its value.
  for (const field of formulaFieldsForEntity(formulaFields, entity)) {
    if (field.type === 'date') {
      add(formulaFieldPath(field), field.type);
    }
  }
  return (ref) => types.get(ref);
}

const VALUE_TYPES = new Set<string>(['number', 'string', 'boolean', 'date']);

/** The schema-derived entries an entity's formulas may draw from: its records, each followed by its fields. */
function entityEntries(entity: FormulaFieldEntity): FieldRefTarget[] {
  const rowRecord = FORMULA_ROW_RECORD[entity];
  switch (entity) {
    case 'company':
      return [
        recordEntry(rowRecord),
        ...describeFields(metricsSchema().describe().fields, rowRecord, true),
        recordEntry(FORMULA_COMPANY_RECORD),
        ...describeFields(yup.object(companyFields()).describe().fields, FORMULA_COMPANY_RECORD, false),
      ];
    case 'round':
      return [
        recordEntry(rowRecord),
        ...describeFields(yup.object(roundTrackerFields()).describe().fields, rowRecord, true),
      ];
    case 'captableInvestment':
      return [
        recordEntry(rowRecord),
        ...describeFields(yup.object(SecurityReportSchema.base()).describe().fields, rowRecord, true),
      ];
  }
}

/**
 * The /column metas that may override an entity's display names. Their paths
 * are the grid's row paths — the unprefixed key for the entity's own row
 * ("fmv"), "company.<key>" for company attributes — so an entity must only
 * see its own view's columns: ROUND_TRACKER metas name round fields, every
 * other viewType (or none — older fixtures omit it) names company fields,
 * and the security grids have no /column rows at all, so captableInvestment
 * keeps its schema labels.
 */
function columnOverridesForEntity(entity: FormulaFieldEntity, columnsMeta: ColumnMeta[]): ColumnMeta[] {
  if (entity === 'captableInvestment') {
    return [];
  }
  const wantRoundTracker = entity === 'round';
  return columnsMeta.filter(
    (column) => (column.viewType === COMPANY_VIEW_TYPE.ROUND_TRACKER) === wantRoundTracker
  );
}

/**
 * Display paths, parent-first: a record's field is "<record>.<its name>", a
 * list item's member "<list name>.<its name>" — the list on its own, as the
 * item record it stands for inside REDUCE — and a root import just its name.
 */
export function withDisplayPaths(entries: FieldRefTarget[]): FieldRefTarget[] {
  const displayPathByPath = new Map<string, string>();
  const segmentByPath = new Map<string, string>();
  return entries.map((entry) => {
    const own = displaySegmentOf(entry);
    const above = entry.list
      ? segmentByPath.get(entry.list.path)
      : entry.parent === undefined
        ? undefined
        : displayPathByPath.get(entry.parent);
    const displayPath = above === undefined ? own : `${above}.${own}`;
    displayPathByPath.set(entry.path, displayPath);
    segmentByPath.set(entry.path, own);
    return {
      ...entry,
      displayPath,
      list: entry.list && {
        ...entry.list,
        displayPath: displayPathByPath.get(entry.list.path) ?? entry.list.displayPath,
      },
    };
  });
}

/** The metrics-row bucket KPI values are materialized under; KPI entries are `kpiData.<id>`. */
const KPI_DATA_PREFIX = 'kpiData';

/** The KPI config slice the vocabulary needs (quantitative KPIs only — textual ones have no kpiData). */
export type KpiFieldConfig = Pick<
  IField<unknown>,
  'id' | 'displayName' | 'description' | 'periods' | 'sectionType'
>;

const RAW_PERIODS: Partial<Record<KpiConfigPeriod, KpiPeriodKey>> = {
  [KpiConfigPeriod.Month]: 'Month',
  [KpiConfigPeriod.Quarter]: 'Quarter',
  [KpiConfigPeriod.Year]: 'Year',
};

/**
 * The raw periods to offer and validate for a KPI. `periods` is a grid-display
 * setting (End-of-Period KPIs get pinned to [latest]), so a config with no raw
 * period still has raw kpiData buckets — fall back to all three.
 */
export function kpiAvailablePeriods(periods: KpiConfigPeriod[] | undefined): KpiPeriodKey[] {
  const configured = new Set((periods ?? []).map((period) => RAW_PERIODS[period]));
  const raw = KPI_PERIOD_KEYS.filter((key) => configured.has(key));
  return raw.length ? raw : [...KPI_PERIOD_KEYS];
}

/** The sections a KPI's Scenario setting (`sectionType`) provides; unset means both. */
export function kpiAvailableSections(sectionType: KpiConfigSection | undefined): KpiSectionKey[] {
  if (sectionType === KpiConfigSection.Actual) {
    return ['actual'];
  }
  if (sectionType === KpiConfigSection.Budget) {
    return ['budget'];
  }
  return ['actual', 'budget'];
}

/**
 * Synthesized targets for KPI's period and scenario arguments: menu rows and
 * chips, not vocabulary entries — their paths are the engine's canonical
 * tokens, resolved by KPI() itself rather than the field indexes. Sections
 * list Actual first, the order the scenario menu shows.
 */
export const KPI_PERIOD_TARGETS: FieldRefTarget[] = KPI_PERIOD_KEYS.map((key) => ({
  path: key,
  displayPath: key,
  displayName: key,
  source: 'kpi',
  type: 'period',
  referenceable: true,
  kpiToken: true,
  description: `The KPI's ${key.toLowerCase()}ly value.`,
}));

export const KPI_SECTION_TARGETS: FieldRefTarget[] = KPI_SECTION_KEYS.map((key) => ({
  path: key,
  displayPath: kpiSectionLabel(key),
  displayName: kpiSectionLabel(key),
  source: 'kpi',
  type: 'scenario',
  referenceable: true,
  kpiToken: true,
  description: key === 'actual' ? 'Reported values.' : 'Budgeted values.',
}));

/**
 * The formula vocabulary is schema-derived per entity, one record per source
 * (company: Metrics.schema first-level fields under "metrics." + Company.schema
 * fields under "company."; round: RoundTracker.schema under "round.";
 * captableInvestment: SecurityReport.schema under "security."), plus the
 * root-level imports: one entry per same-entity formula field (under
 * "formulaData." — formulas may reference each other, but only within their
 * entity: values are materialized per row type, so another entity's results
 * never exist on these rows), and — for the company entity only — one per
 * quantitative KPI (under "kpiData.", referenced through KPI(); round and
 * security rows carry no kpiData). The /column metas only override display
 * names so the menu matches the grid headers where a column exists.
 * `excludeKey` drops that formula field, so the field being edited can't
 * reference itself. customData is deliberately absent for now — dynamic keys
 * need their own resolution story.
 */
export function buildFormulaVocabulary(
  columnsMeta: ColumnMeta[],
  formulaFields: IFormulaFieldDataModel[] = [],
  options?: { entity?: FormulaFieldEntity; excludeKey?: string; kpis?: KpiFieldConfig[] }
): FormulaVocabulary {
  const entity = options?.entity ?? 'company';
  const columnsByPath = new Map<string, { displayName: string; description?: string }>();
  for (const column of columnOverridesForEntity(entity, columnsMeta)) {
    if (column.path && column.displayName && !columnsByPath.has(column.path)) {
      columnsByPath.set(column.path, {
        displayName: column.displayName.trim(),
        description: column.description?.trim() || undefined,
      });
    }
  }

  // Columns are keyed by the grid's row path: the legacy (unprefixed) path for
  // the entity's own row, the canonical one for company attributes.
  const columnFor = (entry: FieldRefTarget) => columnsByPath.get(entry.legacyPath ?? entry.path);
  const schemaEntries = entityEntries(entity);
  const finalNameByPath = new Map(
    schemaEntries.map((entry) => [
      entry.path,
      isFormulaRecord(entry)
        ? entry.displayName
        : (columnFor(entry)?.displayName ?? fixAcronyms(entry.displayName)),
    ])
  );
  const entries: FieldRefTarget[] = schemaEntries.map((entry) => ({
    ...entry,
    displayName: finalNameByPath.get(entry.path) ?? entry.displayName,
    description: columnFor(entry)?.description,
    // The list tag names the list as displayed, so messages and menu labels match its entry.
    list: entry.list && {
      ...entry.list,
      displayName: finalNameByPath.get(entry.list.path) ?? entry.list.displayName,
    },
  }));

  // Formula-field names are authoritative from the model — no column override.
  for (const field of formulaFieldsForEntity(formulaFields, entity)) {
    if (field.key === options?.excludeKey) {
      continue;
    }
    entries.push({
      path: formulaFieldPath(field),
      displayPath: '',
      displayName: field.displayName,
      source: 'formula',
      type: field.type,
      referenceable: true,
      description: field.description || undefined,
    });
  }

  // KPI names are authoritative from the config too. Referenceable, but only
  // ever offered in KPI(…)'s name slot; typecheck teaches the placement when
  // one is typed bare. Company-only: KPI() reads kpiData off the metrics row,
  // which round and security rows do not have.
  for (const kpi of entity === 'company' ? (options?.kpis ?? []) : []) {
    entries.push({
      path: `${KPI_DATA_PREFIX}.${kpi.id}`,
      displayPath: '',
      displayName: kpi.displayName,
      source: 'kpi',
      type: 'number',
      referenceable: true,
      description: kpi.description || undefined,
      kpi: {
        periods: kpiAvailablePeriods(kpi.periods),
        sections: kpiAvailableSections(kpi.sectionType),
      },
    });
  }

  return indexVocabulary(withDisplayPaths(entries));
}

/** The lookup structures over a finished entry list — shared with test fixtures. */
export function indexVocabulary(entries: FieldRefTarget[]): FormulaVocabulary {
  const displayNameIndex = new Map<string, FieldRefTarget[]>();
  const displayPathIndex = new Map<string, FieldRefTarget[]>();
  const byPath = new Map<string, FieldRefTarget>();
  for (const entry of entries) {
    if (isFormulaRecord(entry)) {
      continue;
    }
    const name = normalizeDisplaySegment(entry.displayName).toLowerCase();
    displayNameIndex.set(name, [...(displayNameIndex.get(name) ?? []), entry]);
  }
  for (const entry of entries) {
    const key = displayPathKey(entry.displayPath);
    displayPathIndex.set(key, [...(displayPathIndex.get(key) ?? []), entry]);
    if (entry.legacyPath !== undefined) {
      byPath.set(entry.legacyPath, entry);
    }
  }
  // Canonical paths last, so one wins the (rare) collision with a legacy alias.
  for (const entry of entries) {
    byPath.set(entry.path, entry);
  }
  return {
    records: entries.filter(isFormulaRecord),
    referenceable: entries.filter((entry) => entry.referenceable),
    displayNameIndex,
    displayPathIndex,
    byPath,
  };
}

/** All three entity vocabularies at once — for forms whose entity is a live selection. */
export function buildAllFormulaVocabularies(
  columnsMeta: ColumnMeta[],
  formulaFields: IFormulaFieldDataModel[] = [],
  options?: { excludeKey?: string; kpis?: KpiFieldConfig[] }
): Record<FormulaFieldEntity, FormulaVocabulary> {
  return Object.fromEntries(
    FORMULA_FIELD_ENTITIES.map((entity) => [
      entity,
      buildFormulaVocabulary(columnsMeta, formulaFields, { ...options, entity }),
    ])
  ) as Record<FormulaFieldEntity, FormulaVocabulary>;
}
